import type { Id } from "@repo/convex/dataModel";
import type { AssistantTranscriptNarration, AssistantTranscriptTimelineItem } from "../types";
import { AssistantActivityTurn } from "./assistant-activity-turn";
import { AssistantAskUserSummary } from "./assistant-ask-user-summary";
import { AssistantContextCompactionDivider } from "./assistant-context-compaction-divider";
import { AssistantMessageRow } from "./assistant-message-row";
import { AssistantOperationApprovalCard } from "./assistant-operation-approval-card";
import { AssistantTranscriptMarkerDivider } from "./assistant-transcript-marker-divider";

interface AssistantTranscriptTimelineItemProps {
  item: AssistantTranscriptTimelineItem;
  compact?: boolean;
  projectId: Id<"projects">;
  threadId: string | null;
  narration?: AssistantTranscriptNarration | null;
  hasActiveAssistantMessage?: boolean;
  /** This item is the answer whose `Next steps` options are still live. */
  interactiveNextSteps?: boolean;
}

/**
 * Keyed on the prompt rather than the run so the optimistic "Thinking..." card and the canonical
 * turn that replaces it keep one React identity. Later segments of the run add their seam.
 */
export function getAssistantTimelineRenderKey(item: AssistantTranscriptTimelineItem) {
  if (item.kind !== "activity_turn") {
    return item.key;
  }
  const segmentIndex = item.segmentIndex ?? 0;
  return segmentIndex === 0
    ? `activity-turn:${item.promptMessageId}`
    : `activity-turn:${item.promptMessageId}:${item.segmentKind ?? "steering"}:${segmentIndex}`;
}

function getRunControlMarkerText(
  item: Extract<AssistantTranscriptTimelineItem, { kind: "run_control" }>
) {
  if (item.state === "stopped") {
    return "You stopped the run";
  }
  if (item.state === "aborted") {
    return "You aborted the run";
  }
  if (item.state === "cancelled") {
    return "You cancelled the run before it started";
  }
  if (item.state === "queued") {
    // Queued-input notices ("re-sent as a new message", "not answered") are the one state whose
    // copy is authored server-side. A row still waiting renders as the user's own bubble instead.
    return item.text;
  }
  return "You resumed the run";
}

export function AssistantTranscriptTimelineItemRow({
  item,
  compact,
  projectId,
  threadId,
  narration,
  hasActiveAssistantMessage = false,
  interactiveNextSteps = false,
}: AssistantTranscriptTimelineItemProps) {
  if (item.kind === "message") {
    if (!threadId) return null;
    return (
      <AssistantMessageRow
        message={item}
        compact={compact}
        projectId={projectId}
        threadId={threadId}
        interactiveNextSteps={interactiveNextSteps}
      />
    );
  }

  if (item.kind === "activity_turn") {
    return (
      <AssistantActivityTurn
        item={item}
        projectId={projectId}
        narration={
          item.status === "in_progress" && narration?.promptMessageId === item.promptMessageId
            ? narration
            : null
        }
        hasActiveAssistantMessage={
          item.status === "in_progress" ? hasActiveAssistantMessage : false
        }
        compact={compact}
      />
    );
  }

  if (item.kind === "run_control") {
    return (
      <AssistantTranscriptMarkerDivider
        text={getRunControlMarkerText(item)}
        compact={compact}
        testId="assistant-run-control-marker"
        labelTestId="assistant-run-control-marker-label"
        data-control-kind={item.controlKind}
        data-control-state={item.state}
      />
    );
  }

  if (item.kind === "context_compaction") {
    return <AssistantContextCompactionDivider item={item} compact={compact} />;
  }

  if (item.kind === "operation_approval") {
    return <AssistantOperationApprovalCard projectId={projectId} item={item} compact={compact} />;
  }

  if (item.kind === "assistant_ask_user") {
    return <AssistantAskUserSummary item={item} />;
  }

  // Every kind `listTranscriptTimeline` projects is handled above. A row of any other kind is not
  // something to guess at: rendering it as whatever the last branch happens to be turns an unknown
  // shape into a crash inside a component that was handed the wrong payload.
  return null;
}
